import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createAsyncStorage } from '@/lib/asyncStorage'
import { chromeStorage } from '@/lib/storage'

export interface WeekTask {
  id: string
  text: string
  done: boolean
  priority: 'high' | 'medium' | 'low'
  dayIndex: number // 0-6 for the days of week
  createdAt: string // ISO date
}

export interface WeekData {
  id: string // year-week format
  days: {
    [key: string]: WeekTask[]
  }
}

export interface WeeklyState {
  weeks: WeekData[]
  addWeek: (week: WeekData) => void
  addTask: (weekId: string, day: string, task: Omit<WeekTask, 'id' | 'createdAt'> & { id?: string; createdAt?: string }) => void
  toggleTask: (weekId: string, day: string, taskId: string) => void
  deleteTask: (weekId: string, day: string, taskId: string) => void
  getWeek: (id: string) => WeekData | undefined
  updateTask: (weekId: string, day: string, taskId: string, text: string) => void
  clearAllWeeks: () => void
}

export const useWeeklyStore = create<WeeklyState>()(
  persist(
    (set, get) => ({
      weeks: [],
      addWeek: (week) =>
        set((state) => ({
          weeks: [...state.weeks, week],
        })),
      addTask: (weekId, day, taskData) =>
        set((state) => {
          const task: WeekTask = {
            ...(taskData as Omit<WeekTask, 'id' | 'createdAt'>),
            id: taskData.id || `task-${Date.now()}`,
            createdAt: taskData.createdAt || new Date().toISOString(),
          }
          const weeks = Array.isArray(state.weeks) ? state.weeks : []
          console.log('🔧 addTask - before:', { weekId, day, weeks: weeks.length })
          
          // Check if week exists
          let weekExists = weeks.find((w) => w.id === weekId)
          
          let updatedWeeks: WeekData[]
          if (weekExists) {
            // Week exists - update it
            updatedWeeks = weeks.map((w) =>
              w.id === weekId
                ? {
                    ...w,
                    days: {
                      ...w.days,
                      [day]: [...(w.days[day] || []), task],
                    },
                  }
                : w
            )
          } else {
            // Week doesn't exist - create it
            const newWeek: WeekData = {
              id: weekId,
              days: { 0: [], 1: [], 2: [], 3: [], 4: [], 5: [], 6: [] },
            }
            newWeek.days[day] = [task]
            updatedWeeks = [...weeks, newWeek]
            console.log('🎯 Created new week:', weekId)
          }
          
          console.log('🔧 addTask - after:', { updatedWeeks: updatedWeeks.length, taskAdded: updatedWeeks.find(w => w.id === weekId)?.days })
          return { weeks: updatedWeeks }
        }),
      toggleTask: (weekId, day, taskId) =>
        set((state) => {
          const weeks = Array.isArray(state.weeks) ? state.weeks : []
          return {
            weeks: weeks.map((w) =>
              w.id === weekId
                ? {
                    ...w,
                    days: {
                      ...w.days,
                      [day]: w.days[day]?.map((t) =>
                        t.id === taskId ? { ...t, done: !t.done } : t
                      ) || [],
                    },
                  }
                : w
            ),
          }
        }),
      deleteTask: (weekId, day, taskId) =>
        set((state) => {
          const weeks = Array.isArray(state.weeks) ? state.weeks : []
          return {
            weeks: weeks.map((w) =>
              w.id === weekId
                ? {
                    ...w,
                    days: {
                      ...w.days,
                      [day]: w.days[day]?.filter((t) => t.id !== taskId) || [],
                    },
                  }
                : w
            ),
          }
        }),
      getWeek: (id) => get().weeks.find((w) => w.id === id),
      updateTask: (weekId, day, taskId, text) =>
        set((state) => ({
          weeks: state.weeks.map((w) =>
            w.id === weekId
              ? {
                  ...w,
                  days: {
                    ...w.days,
                    [day]: w.days[day]?.map((t) =>
                      t.id === taskId ? { ...t, text } : t
                    ) || [],
                  },
                }
              : w
          ),
        })),
      clearAllWeeks: () => {
        set({ weeks: [] })
      },
    }),
    {
      name: 'weeks',
      storage: createAsyncStorage(),
    }
  )
)

export const initializeWeeks = async (): Promise<void> => {
  try {
    console.log('📚 Initializing weeks...')
    // Rehydrate from persisted storage
    await useWeeklyStore.persist.rehydrate()
    console.log('✅ Weekly planner rehydrated from storage')
  } catch (error) {
    console.error('❌ Failed to initialize weeks:', error)
  }
}
